/**
 * Render an ML identification suggestion for a single image.
 * Shared between the Intake review screen, the Capture_Batch reviewer, and
 * the Catalog detail page.
 *
 * Under physical-first-card-tracking the ML output is a non-authoritative
 * `Card_Suggestion` (Requirement 2): it is labeled here as a suggestion and is
 * never presented as the card's verified identity (Requirements 7.4).
 *
 * Validates: Requirements 2.3, 5.3, 7.4, 12.12
 */

import type { CardIdentity, IdentificationResponse, IdentityStatus } from './types';

interface PredictionViewProps {
  identification: IdentificationResponse;
  /** Optional caption displayed above the panel. Defaults to a suggestion label. */
  title?: string;
}

/**
 * A flat read-only display of an identification suggestion. The
 * heading and field labels make clear this is an unverified ML suggestion
 * rather than the card's authoritative identity.
 */
export function PredictionView({ identification, title }: PredictionViewProps): JSX.Element {
  return (
    <section className="tcvs-card">
      <h3 style={{ marginTop: 0, marginBottom: 4 }}>{title ?? 'Suggested (unverified)'}</h3>
      <p className="tcvs-muted" style={{ marginTop: 0 }}>
        Machine-learning suggestion — not a verified identity.
      </p>
      <IdentitySummary identification={identification} />
    </section>
  );
}

function IdentitySummary({
  identification,
}: {
  identification: IdentificationResponse;
}): JSX.Element {
  if (identification.status === 'known') {
    return (
      <div>
        <p style={{ marginBottom: 4 }}>
          <strong>Suggested identity:</strong> {formatIdentity(identification.identity)}
        </p>
        <p style={{ margin: 0 }}>
          <strong>Confidence:</strong> {formatConfidence(identification.confidence)}
        </p>
      </div>
    );
  }
  return (
    <div>
      <p style={{ marginBottom: 4 }}>
        <strong>Suggested identity:</strong> Unknown (top confidence{' '}
        {formatConfidence(identification.confidence)})
      </p>
      <p style={{ marginTop: 0, marginBottom: 4 }}>
        <strong>Top candidates:</strong>
      </p>
      <ol style={{ marginTop: 0 }}>
        {identification.candidates.map((c, i) => (
          <li key={`${c.identity.category}-${c.identity.set}-${c.identity.number}-${i}`}>
            {formatIdentity(c.identity)} — {formatConfidence(c.confidence)}
          </li>
        ))}
      </ol>
    </div>
  );
}

/**
 * A compact identity-status badge distinguishing an `UNIDENTIFIED` (unreviewed)
 * card from an `IDENTIFIED` (human-verified) one (Requirement 7.3).
 */
export function IdentityStatusBadge({ status }: { status: IdentityStatus }): JSX.Element {
  const identified = status === 'IDENTIFIED';
  return (
    <strong className={identified ? 'tcvs-tone-stored' : 'tcvs-tone-unplaced'}>
      {identified ? 'Identified' : 'Unidentified'}
    </strong>
  );
}

/** Pretty-print a card identity for display. */
export function formatIdentity(identity: CardIdentity): string {
  return `${identity.category} · ${identity.set} #${identity.number} ${identity.name}`;
}

/** Format a confidence score as a percentage (e.g. `0.873` → `87.3%`). */
export function formatConfidence(confidence: number): string {
  if (!Number.isFinite(confidence)) return 'n/a';
  return `${(confidence * 100).toFixed(1)}%`;
}
